import { useAlert } from '@ads/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getApiV1ExamGridQueryKey,
  useGetApiV1ExamExamGradeCriteria,
  useGetApiV1ExamExamReferenceShow,
  useGetApiV1ExamGridExamid,
  usePostApiV1Exam,
  usePutApiV1ExamId,
} from '@/shared/api/generated';
import type { ExamFormData } from '../types/exam';

const defaultFormData: ExamFormData = {
  title: '',
  instruction: '',
  examType: null,
  companyId: null,
  companyName: null,
  disciplineId: null,
  disciplineName: null,
  gradeCriteria: null,
  weight: 1,
  examReferenceShow: null,
  examReferenceShowPost: null,
  showAnswersTries: false,
  randomize: false,
  questionsQuantity: null,
  percentualComplexity: 0,
  recommendedToDoOnPortal: false,
  enableSafeEvaluationMode: false,
  maxScreenExitsAllowed: 3,
  enableBlockUnauthorizedActions: false,
};

/**
 * Hook para criar ou editar uma prova via API.
 *
 * Submete os dados para POST /api/v1/exam (criação) ou PUT /api/v1/exam/:id
 * (edição) e invalida o cache do grid após sucesso.
 *
 * @param examId - ID da prova para edição; omitir para criação
 * @returns Estado do formulário e handlers de submit/cancelar
 *
 * @example
 * ```tsx
 * const { form, updateForm, onSubmit, isSubmitting } = useExamForm(id);
 * ```
 */
export function useExamForm(examId?: string) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = Boolean(examId);
  const numId = Number(examId);

  const gridQuery = useGetApiV1ExamGridExamid(numId, { query: { enabled: isEditing } });
  const existing = isEditing ? gridQuery.data : null;

  const { data: referenceShowOptions = [] } = useGetApiV1ExamExamReferenceShow({
    query: { enabled: isEditing },
  });
  const { data: gradeCriteriaOptions = [] } = useGetApiV1ExamExamGradeCriteria({
    query: { enabled: isEditing },
  });

  const [formInitialized, setFormInitialized] = useState(false);
  const [form, setForm] = useState<ExamFormData>(defaultFormData);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const { alert } = useAlert();

  const { t } = useTranslation('examManagement');

  useEffect(() => {
    if (existing && !formInitialized) {
      setForm(prev => ({
        ...prev,
        title: existing.title ?? '',
        examType: existing.examType ?? null,
        disciplineId: existing.disciplineId ?? null,
        disciplineName: existing.disciplineText ?? null,
        recommendedToDoOnPortal: existing.recommendedToDoOnPortal ?? false,
        enableSafeEvaluationMode: existing.enableSafeEvaluationMode ?? false,
        maxScreenExitsAllowed: existing.maxScreenExitsAllowed ?? 3,
      }));
      setFormInitialized(true);
    }
  }, [existing, formInitialized]);

  useEffect(() => {
    if (formInitialized && referenceShowOptions.length > 0 && gradeCriteriaOptions.length > 0) {
      const firstRefShow = referenceShowOptions[0]?.key ?? null;
      const firstGradeCriteria = gradeCriteriaOptions[0]?.key ?? null;
      setForm(prev => ({
        ...prev,
        gradeCriteria: prev.gradeCriteria ?? firstGradeCriteria,
        examReferenceShow: prev.examReferenceShow ?? firstRefShow,
        examReferenceShowPost: prev.examReferenceShowPost ?? firstRefShow,
      }));
    }
  }, [formInitialized, referenceShowOptions, gradeCriteriaOptions]);

  const createMutation = usePostApiV1Exam({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getApiV1ExamGridQueryKey() });
        void navigate({ to: '/exams' as never });
      },
      onError: () =>
        alert({
          alertType: 'warning',
          title: t('toast.error'),
        }),
    },
  });

  const updateMutation = usePutApiV1ExamId({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getApiV1ExamGridQueryKey() });
        alert({
          alertType: 'success',
          title: t('toast.updateSuccess'),
        });
      },
      onError: () =>
        alert({
          alertType: 'warning',
          title: t('toast.error'),
        }),
    },
  });

  const updateForm = useCallback((partial: Partial<ExamFormData>) => {
    setForm(prev => ({ ...prev, ...partial }));
  }, []);

  const onSubmit = useCallback(() => {
    setSubmitStatus('idle');
    const payload = {
      title: form.title,
      // Na edição o `instruction` não é enviado: o campo fica oculto na aba Geral
      // porque o GET /exam/grid/{id} não retorna o valor salvo — enviá-lo (vazio)
      // sobrescreveria a instrução existente. O campo permanece na criação.
      ...(!isEditing && { instruction: form.instruction || null }),
      disciplineId: form.disciplineId,
      // O GET /exam/grid/{id} não retorna a empresa, então na edição só enviamos
      // `companyId` quando o usuário selecionou um valor — evita sobrescrever o
      // vínculo existente com null. Na criação o campo é sempre enviado.
      ...((!isEditing || form.companyId !== null) && { companyId: form.companyId }),
      ...(form.examType !== null && { examType: form.examType }),
      ...(form.gradeCriteria !== null && { gradeCriteria: form.gradeCriteria }),
      ...(form.examReferenceShow !== null && { examReferenceShow: form.examReferenceShow }),
      ...(form.examReferenceShowPost !== null && {
        examReferenceShowPost: form.examReferenceShowPost,
      }),
      showAnswersTries: form.showAnswersTries,
      weight: form.weight,
      active: true,
      randomize: form.randomize,
      questionsQuantity: form.questionsQuantity,
      percentualComplexity: form.percentualComplexity,
      recommendedToDoOnPortal: form.recommendedToDoOnPortal,
      enableBlockUnauthorizedActions: form.enableBlockUnauthorizedActions,
      enableSafeEvaluationMode: form.enableSafeEvaluationMode,
      maxScreenExitsAllowed: form.maxScreenExitsAllowed,
    };

    if (isEditing && numId) {
      updateMutation.mutate({ id: numId, data: payload });
    } else {
      createMutation.mutate({ data: payload });
    }
  }, [form, isEditing, numId, createMutation, updateMutation]);

  const onCancel = useCallback(() => {
    void navigate({ to: '/exams' as never });
  }, [navigate]);

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return {
    form,
    updateForm,
    isEditing,
    isSubmitting,
    submitStatus,
    onSubmit,
    onCancel,
  } as const;
}
